import {useCallback, useEffect} from 'react';

import afterFrame from '../../afterFrame';
import {addFirstTiming, hasTriggeredTiming, useDatadogRum} from '../../datadog';

type Predicate = () => boolean;

type UseFirstTimingHook = () => void;

/**
 * A version of Datadog's addTiming function that only fires once. It returns a
 * function that, when called, will trigger datadog to track when the timing
 * occurred. It will record only the first time a particular metric is called
 * per-view. So if multiple components trigger the same timing, only the first
 * one will be noted by Datadog RUM.
 *
 * If you want to track when a component mounts on the page for the first time,
 * you can combine `useFirstTiming` and `useLayoutEffect` to achieve that:
 *
 * @example
 * ```js
 * useLayoutEffect(useFirstTiming('my_component_mounted'))
 * ```
 *
 * @param metric
 * @param logAfterFrame
 */
export function useFirstTiming(
  metric: string,
  logAfterFrame = true
): UseFirstTimingHook {
  const datadog = useDatadogRum();

  return useCallback(() => {
    addFirstTiming(datadog, metric);
    if (logAfterFrame) {
      afterFrame(() => {
        addFirstTiming(datadog, `${metric}-afterFrame`);
      });
    }
  }, [datadog, metric, logAfterFrame]);
}

/**
 * Triggers Datadog's addTiming function for the provided metric the first time
 * the predicate function returns true.
 *
 * @example
 * ```js
 * const { data, loading } = useQuery(...)
 * useFirstTimingEventWhen('my_component_loaded', () => !loading)
 * ```
 *
 * @param metric
 * @param predicate
 * @param logAfterFrame
 */
export function useFirstTimingEventWhen(
  metric: string,
  predicate: Predicate,
  logAfterFrame = true
): void {
  const datadog = useDatadogRum();

  useEffect(() => {
    if (!hasTriggeredTiming(metric) && predicate()) {
      addFirstTiming(datadog, metric);
      if (logAfterFrame) {
        afterFrame(() => {
          addFirstTiming(datadog, `${metric}-afterFrame`);
        });
      }
    }
  });
}
